<template>
    <div :class="containerClass">
        <div class="flex items-center gap-[6px]" :class="headerClass">
            <CreditCard class="h-[14px] w-[14px]" :class="iconClass" />
            <span
                class="text-[12.25px] font-medium leading-[142%]"
                :class="titleClass"
            >
                {{ title }}
            </span>
        </div>

        <p
            class="mt-[4px] text-[10.5px] leading-[140%]"
            :class="[messageClass, textClass]"
        >
            {{ message }}
        </p>

        <ul
            v-if="showItems && items.length"
            class="mt-[9px] list-disc space-y-[4px] pl-[14px] text-[10.5px] leading-[140%]"
            :class="[messageClass, listClass]"
        >
            <li v-for="item in items" :key="item.label" :class="textClass">
                <span class="font-medium">{{ item.label }}</span>
                {{ item.value }}
            </li>
        </ul>

        <label
            v-if="agreementText && showAgreement"
            class="mt-[12px] flex items-center gap-[6px] text-[10.5px] leading-[140%] text-[#6B7280]"
            :class="agreementClass"
        >
            <input
                type="checkbox"
                class="h-[11px] w-[11px] rounded-[3px] border border-[#9CA3AF] text-[#088BEA] focus:ring-1 focus:ring-[#088BEA]"
                :checked="modelValue"
                @change="onAgreementChange"
            />
            <span>{{ agreementText }}</span>
        </label>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { CreditCard } from "lucide-vue-next";

type BillingItem = {
    label: string;
    value: string;
};

const props = defineProps<{
    title?: string;
    message: string;
    items: BillingItem[];
    agreementText?: string;
    modelValue: boolean;
    variant?: "warning" | "danger";
    showItems?: boolean;
    showAgreement?: boolean;
    surfaceVariant?: "default" | "white";
    align?: "center" | "left";
    fullWidth?: boolean;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: boolean): void;
}>();

const title = computed(() => props.title ?? "Billing Update");
const variant = computed(() => props.variant ?? "warning");
const showItems = computed(() => props.showItems !== false);
const showAgreement = computed(() => props.showAgreement !== false);
const surfaceVariant = computed(() => props.surfaceVariant ?? "default");
const align = computed(() => props.align ?? "center");

const containerClass = computed(() =>
    [
        "rounded-[6.75px] border-[1px] px-[21px] py-[14px]",
        props.fullWidth === true ? "w-full" : "",
        align.value === "left" ? "text-left" : "",
        surfaceVariant.value === "white"
            ? "border-white bg-white"
            : variant.value === "danger"
              ? "border-[#FCA5A5] bg-[#FEE2E2]"
              : "border-[#FEF9C2] bg-[#FEFCE8]",
    ].filter(Boolean),
);

const iconClass = computed(() =>
    variant.value === "danger" ? "text-[#B91C1C]" : "text-[#744210]",
);

const titleClass = computed(() =>
    variant.value === "danger" ? "text-[#B91C1C]" : "text-[#744210]",
);

const messageClass = computed(() =>
    variant.value === "danger" ? "text-[#7F1D1D]" : "text-[#744210]",
);
const headerClass = computed(() =>
    align.value === "left" ? "justify-start" : "",
);
const textClass = computed(() => (align.value === "left" ? "text-left" : ""));
const listClass = computed(() =>
    align.value === "left" ? "mr-auto w-full text-left" : "",
);
const agreementClass = computed(() =>
    align.value === "left" ? "items-start text-left" : "",
);

function onAgreementChange(event: Event) {
    const target = event.target as HTMLInputElement | null;
    emit("update:modelValue", Boolean(target?.checked));
}
</script>
